import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { ApiFehler, hole, schicke } from '../lib/api'
import { alterBerechnen } from '../lib/datum'
import { mitgliedsFarbe, rollenName } from '../session/logic'
import { useFab } from '../shell/FabSlot'
import { Avatar, Button, Card, Empty, ListItem, Select, Sheet, TextField, useToast } from '../ui'
import type { Role } from '@famora/core'

interface Mitglied {
  id: string
  displayName: string
  role: Role
  color: string
  birthDate?: string | null
  status: 'active' | 'invited' | 'disabled'
  hasLogin: boolean
  loginMethod: 'google' | 'code' | null
}

export function Familie() {
  const qc = useQueryClient()
  const zeigeToast = useToast()
  const [offen, setOffen] = useState(false)
  const [name, setName] = useState('')
  const [rolle, setRolle] = useState<Role>('guest')
  const [geburtsdatum, setGeburtsdatum] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const liste = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<Mitglied[]>('/api/members'),
  })

  const anlegen = useMutation({
    mutationFn: () =>
      schicke<Mitglied>('POST', '/api/members', {
        displayName: name,
        role: rolle,
        ...(geburtsdatum ? { birthDate: geburtsdatum } : {}),
      }),
    onSuccess: async (m) => {
      setName('')
      setRolle('guest')
      setGeburtsdatum('')
      setOffen(false)
      setFehler(null)
      await qc.invalidateQueries({ queryKey: ['members'] })
      zeigeToast(`Person „${m.displayName}“ hinzugefügt`, 'erfolg')
    },
    onError: (f: unknown) => {
      const msg = f instanceof ApiFehler ? f.message : 'Anlegen fehlgeschlagen.'
      setFehler(msg)
      zeigeToast(msg, 'error')
    },
  })

  useFab({
    label: 'Person hinzufügen',
    icon: 'plus',
    onClick: () => {
      setOffen(true)
    },
  })

  const aktive = (liste.data ?? []).filter((m) => m.status !== 'disabled')
  const ruhende = (liste.data ?? []).filter((m) => m.status === 'disabled')

  if (liste.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void liste.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Familie</h1>
          <p>Wer dazugehört und was jede Person sehen darf.</p>
        </div>
      </div>

      {liste.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {!liste.isPending && aktive.length === 0 ? (
        <Empty
          titel="Noch niemand da"
          text="Der Knopf unten rechts legt die erste Person an."
        />
      ) : null}

      {aktive.length > 0 ? (
        <Card titel="Aktiv">
          <ul className="liste liste--gruppe">
            {aktive.map((m) => {
              const farbe = mitgliedsFarbe(m.color, m.id)
              const alter = alterBerechnen(m.birthDate)
              const alterText = alter !== null ? ` · ${alter} ${alter === 1 ? 'Jahr' : 'Jahre'}` : ''
              return (
                <li key={m.id}>
                  {/* Die ganze Zeile führt zu den Rechten - ein Verweis, kein
                      Knopf: Adresse kopieren, in neuem Reiter öffnen und
                      Zurück funktionieren dann wie überall sonst. */}
                  <Link to={`/familie/${m.id}`} className="zeilen-verweis">
                    <ListItem
                      as="div"
                      titel={m.displayName}
                      unterzeile={`${rollenName(m.role)}${alterText}${m.hasLogin ? '' : ' · noch kein Zugang'}`}
                      farbe={farbe}
                      fuehrend={<Avatar name={m.displayName} farbe={farbe} />}
                      folgend={<span className="unterzeile">Rechte</span>}
                    />
                  </Link>
                </li>
              )
            })}
          </ul>
        </Card>
      ) : null}

      {ruhende.length > 0 ? (
        <Card titel="Nicht mehr aktiv">
          <ul className="liste liste--gruppe">
            {ruhende.map((m) => (
              <li key={m.id}>
                <Link to={`/familie/${m.id}`} className="zeilen-verweis">
                  <ListItem
                    as="div"
                    titel={m.displayName}
                    unterzeile={`${rollenName(m.role)} · Deaktiviert`}
                    erledigt
                    folgend={<span className="unterzeile">Bearbeiten</span>}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Sheet
        offen={offen}
        titel="Person hinzufügen"
        onSchliessen={() => {
          setOffen(false)
        }}
      >
        <form
          className="stack-4"
          onSubmit={(e) => {
            e.preventDefault()
            anlegen.mutate()
          }}
        >
          <TextField
            id="neu-name"
            label="Name"
            wert={name}
            onWert={setName}
            required
            autoFocus
          />
          <Select
            id="neu-rolle"
            label="Rolle"
            wert={rolle}
            onWert={(w) => {
              setRolle(w as Role)
            }}
          >
            <option value="guest">Gast — sieht nur, was du freischaltest</option>
            <option value="child">Kind</option>
            <option value="teen">Jugendlich</option>
            <option value="adult">Erwachsen</option>
          </Select>
          <TextField
            id="neu-geburt"
            type="date"
            label="Geburtsdatum (optional)"
            wert={geburtsdatum}
            onWert={setGeburtsdatum}
          />
          {fehler ? (
            <p className="fehler" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="blatt__fuss-inline">
            <Button
              variant="text"
              onClick={() => {
                setOffen(false)
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit" laeuft={anlegen.isPending} disabled={name.trim().length === 0}>
              Person anlegen
            </Button>
          </div>
        </form>
      </Sheet>
    </>
  )
}
